import type { Order } from '@modules/Orders/ViewOrders/models/Order';
import type { DuplicateOrderData } from '@shared/services/DuplicateOrderService';
import { PackingApiAdapter } from '@modules/OrderPreparation/Packing/adapters/PackingApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

// Tipo flexible que acepta ambos formatos de dirección
type FlexibleOrder = {
  id: number;
  ref: string;
  shop_id: number;
  payment_method_id: number;
  country_id?: number;
  customer: {
    name?: string;
    phone?: string;
    email?: string;
    address: {
      address1?: string; // Formato List
      address_1?: string; // Formato ViewOrders
      address2?: string; // Formato List
      address_2?: string; // Formato ViewOrders
      zip?: string;
      city?: string;
      province?: string;
      country_code?: string;
    };
  };
  lines: Array<{
    product: {
      id: number;
      code: string;
      image_hash: string;
    };
    name: string;
    amount: number;
    qty: number;
  }>;
};

/**
 * Adaptador para convertir una Order existente al formato requerido
 * por CreateEditOrders para funcionalidad de duplicación
 */
export class OrderDuplicationAdapter {
  /**
   * Convierte una Order completa a DuplicateOrderData
   * Maneja tanto el formato de List como ViewOrders
   * Obtiene los nombres reales de productos y packs desde la API
   */
  static async adaptOrderForDuplication(
    order: FlexibleOrder | Order
  ): Promise<DuplicateOrderData> {
    // Función helper para obtener el valor de dirección correcto
    const getAddressField = (
      field1: string | undefined,
      field2: string | undefined
    ): string => {
      return field1 || field2 || '';
    };

    // Crear instancia del adaptador para hacer llamadas a la API
    const httpClient = createHttpClient(apiClientWH);
    const packingAdapter = new PackingApiAdapter(httpClient);

    // Obtener IDs únicos de productos y packs
    const productIds: number[] = [];
    const packIds: number[] = [];

    order.lines.forEach((line) => {
      if (line.product && line.product.id) {
        productIds.push(line.product.id);
      }
      if (line.pack_id) {
        packIds.push(line.pack_id);
      }
    });

    // Hacer llamadas paralelas a la API para obtener nombres reales
    const [productsData, packsData] = await Promise.all([
      productIds.length > 0
        ? packingAdapter.getProductsByIds(productIds)
        : Promise.resolve([]),
      packIds.length > 0
        ? packingAdapter.getPacksByIds(packIds)
        : Promise.resolve([]),
    ]);

    // Crear mapas para acceso rápido por ID
    const productsMap = new Map(productsData.map((p) => [p.id, p]));
    const packsMap = new Map(packsData.map((p) => [p.id, p]));

    return {
      orderInfo: {
        reference: `${order.ref}-COPY`, // Agregar sufijo para indicar que es copia
        store: order.shop_id?.toString() || '1',
        paymentMethod: order.payment_method_id?.toString() || '1',
      },
      shippingData: {
        customerName: order.customer.name || '',
        customerPhone: order.customer.phone || '',
        customerEmail: order.customer.email || '',
        address1: getAddressField(
          (order.customer.address as any).address1,
          (order.customer.address as any).address_1
        ),
        address2: getAddressField(
          (order.customer.address as any).address2,
          (order.customer.address as any).address_2
        ),
        postalCode: order.customer.address.zip || '',
        city: order.customer.address.city || '',
        province: order.customer.address.province || '',
        country: order.customer.address.country_code || '',
        countryId: '0', // Valor por defecto, el componente lo actualizará automáticamente
      },
      products: order.lines
        .filter(
          (line) =>
            (line.product && line.product.id) || (line.pack && line.pack_id)
        ) // Filtrar líneas con producto o pack válido
        .map((line) => {
          const isPack = !!line.pack_id;
          const id = line.product?.id || line.pack_id || 0;

          // Obtener datos reales desde la API
          const apiData = isPack ? packsMap.get(id) : productsMap.get(id);

          return {
            id,
            name:
              apiData?.name ||
              apiData?.title ||
              line.name ||
              line.pack?.title ||
              '',
            code: apiData?.code || line.product?.code || line.pack?.title || '',
            image:
              apiData?.image_hash ||
              apiData?.image ||
              line.product?.image_hash ||
              '',
            price: line.amount || 0,
            quantity: line.qty || 0,
            type: isPack ? ('pack' as const) : ('product' as const),
            pack_id: line.pack_id || null,
          };
        }),
    };
  }

  /**
   * Genera una referencia única para la orden duplicada
   */
  static generateDuplicateReference(originalRef: string): string {
    return `${originalRef}-COPY`;
  }
}
